<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <h5 class="mb-0"><i class="fa fa-fw fa-camera mr-2" />History Foto / Video</h5>
            <hr />
            <b-row>
                <b-col md="4">
                    <b-form-group label="Search NF">
                        <b-input-group>
                            <b-form-input v-model="filter.noFaktur" maxlength="50" placeholder="Cari No. Faktur..."
                                @keydown.enter="getItems" />
                            <b-input-group-append>
                                <b-button variant="primary" @click="getItems()" :disabled="isBusy">
                                    <b-spinner small v-if="isBusy" type="border" class="mr-2" />
                                    <i v-else class="fa fa-search mr-2" />Cari
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
            </b-row>

            <template v-if="initTable">
                <b-table bordered hover show-empty no-local-sorting :busy="isBusy" :items="items" :fields="fields"
                    @sort-changed="onSortChanged" responsive class="mb-3">

                    <template #table-busy>
                        <div class="d-flex justify-content-center">
                            <b-spinner label="Loading..." />
                        </div>
                    </template>

                    <template v-slot:empty>
                        Tidak ada data history foto/video.
                    </template>
                    <template v-slot:cell(noFakturImei)="row">
                        <strong>{{ row.item.inven_no_faktur }}</strong>
                        <div class="small text-muted">
                            {{ row.item.inven_imei }}
                        </div>
                    </template>
                    <template v-slot:cell(tipe)="row">
                        {{ row.item.history_type }}
                    </template>
                    <template v-slot:cell(staffFv)="row">
                        {{ row.item.actor_name }}
                    </template>
                    <template v-slot:cell(keterangan)="row">
                        {{ row.item.history_description }}
                    </template>
                    <template v-slot:cell(status)="row">
                        <b-badge :variant="getStatusVariant(row.item.history_status)">
                            {{ row.item.history_status }}
                        </b-badge>
                    </template>
                    <template v-slot:cell(tanggal)="row">
                        {{ $helper.parseDate(row.item.action_date) }}, {{
                            $helper.parseHourMinute(row.item.action_date) }}
                    </template>
                    <template v-slot:cell(action)="row">
                        <b-button size="sm" variant="primary" v-b-tooltip.hover.top title="Detail"
                            @click="showDetail(row.item)">
                            <i class="fa fa-fw fa-info" />
                        </b-button>
                    </template>
                </b-table>

                <PageSizer :totalRows="totalRows" :page="currentPage" :options="options" @limit-change="onLimitChange"
                    @page-change="onPageChange" />
            </template>

            <div class="d-flex justify-content-center mt-3" v-if="isLoadingTable && !initTable">
                <b-spinner label="Loading..." />
            </div>

            <div v-if="!initTable && !isLoadingTable" class="text-center mt-4">
                <p class="text-muted">Masukkan No. Faktur dan klik "Cari" untuk menampilkan data history.</p>
            </div>
        </b-card>

        <b-modal id="m-duplicate-nf" title="Pilih Inventaris" size="xl" no-close-on-backdrop no-close-on-esc
            hide-footer>
            <b-table responsive striped hover :items="duplicateItems" :fields="duplicateFields">
                <template v-slot:cell(no_faktur)="{ item }">
                    <strong style="cursor: pointer;" @click="selectInventory(item)">
                        {{ item.id }}
                        <b-button @click="selectInventory(item)" variant="primary" size="sm" class="ml-2">
                            <i class="fa fa-fw fa-search" />
                        </b-button>
                    </strong>
                </template>
                <template v-slot:cell(barang)="{ item }">
                    <strong>{{ item.type || "-" }}</strong>
                    <div class="small text-muted">
                        {{ item.kind || "-" }} - {{ item.brand || "-" }}
                        <span v-if="item.color"> - {{ item.color }}</span>
                    </div>
                </template>
                <template v-slot:cell(spec)="{ item }">
                    {{ item.spec || "-" }}
                </template>
                <template v-slot:cell(imei)="{ item }">
                    {{ item.imei || "-" }}
                </template>
                <template v-slot:cell(status)="{ item }">
                    <b-badge :variant="getInventoryStatusVariant(item.status)">
                        {{ getInventoryStatusName(item.status) }}
                    </b-badge>
                </template>
            </b-table>
        </b-modal>

        <b-modal id="m-detail" title="Detail History Foto/Video" size="xl" hide-footer @hide="onClearMediaPreview">
            <template v-if="activeItem">
                <b-row>
                    <b-col cols="12" lg="8">
                        <div class="media-container">
                            <b-img-lazy v-if="mediaPreview.type === 'image'" class="media-preview"
                                :src="mediaPreview.computedUrl || '/img/img-preview.png'"
                                blank-src="/img/image-loading.gif" block alt="Image Preview" />

                            <video v-else-if="mediaPreview.type === 'video'" class="media-preview" controls
                                :key="mediaPreview.url">
                                <source :src="mediaPreview.url" :type="mediaPreview.mime_type">
                                Your browser does not support the video tag.
                            </video>

                            <b-img-lazy v-else class="media-preview" src="/img/img-preview.png" block alt="No Media" />
                        </div>

                        <div class="media-scroll" v-if="activeItem.assets && activeItem.assets.length > 1">
                            <div class="media-scroll-content" v-for="(asset, index) in activeItem.assets"
                                :key="'asset-' + index" @click="onClickAsset(asset)">
                                <b-img-lazy class="media-scroll-preview" :class="{ active: asset.isActive }"
                                    :src="asset.computedThumbnailUrl || '/img/image-loading.gif'"
                                    blank-src="/img/image-loading.gif" block
                                    :alt="asset.type === 'video' ? 'Video Thumbnail' : 'Image Thumbnail'" />
                            </div>
                        </div>
                    </b-col>

                    <b-col cols="12" lg="4" class="border-responsive">

                        <b-button block variant="primary" class="mt-2" :disabled="!mediaPreview.url"
                            @click="openMediaInNewTab(mediaPreview.url)">
                            Tampilkan Item di Tab Baru
                        </b-button>

                        <div class="mt-3">
                            <strong>Detail Barang</strong>
                            <hr class="mt-1 mb-2" />
                            <div class="detail-info">
                                <div><strong>No. Faktur:</strong> {{ activeItem.inven_no_faktur }}</div>
                                <div><strong>IMEI:</strong> {{ activeItem.inven_imei }}</div>
                                <div><strong>Tipe:</strong> {{ activeItem.inven_type }}</div>
                                <div><strong>Spec:</strong> {{ activeItem.inven_spec || "-" }}</div>
                                <div><strong>Jenis:</strong> {{ activeItem.inven_kind || "-" }}</div>
                                <div><strong>Brand:</strong> {{ activeItem.inven_brand || "-" }}</div>
                                <div v-if="activeItem.inven_color"><strong>Warna:</strong> {{ activeItem.inven_color }}
                                </div>
                            </div>
                        </div>
                        <div class="mt-3">
                            <strong>Detail History</strong>
                            <hr class="mt-1 mb-2" />
                            <div class="detail-info">
                                <div><strong>Tipe History:</strong> {{ activeItem.history_type }}</div>
                                <div><strong>Status:</strong>
                                    <b-badge :variant="getStatusVariant(activeItem.history_status)">
                                        {{ activeItem.history_status }}
                                    </b-badge>
                                </div>
                                <div><strong>Staff FV:</strong> {{ activeItem.actor_name }}</div>
                                <div><strong>Tanggal:</strong> {{ $helper.parseDate(activeItem.action_date) }}, {{
                                    $helper.parseHourMinute(activeItem.action_date) }}</div>
                                <div><strong>Keterangan:</strong></div>
                                <p class="mb-0">{{ activeItem.history_description }}</p>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </template>
        </b-modal>

    </div>
</template>

<script>
export default {
    data() {
        return {
            initStatus: 0,
            initTable: 0,
            isLoadingTable: false,
            isBusy: false,

            filter: {
                noFaktur: ""
            },

            mediaPreview: {
                type: null,
                url: null,
                computedUrl: null,
                url_compressed: null,
                mime_type: null,
                isActive: false
            },

            options: [
                { value: 10, text: '10' },
                { value: 20, text: '20' },
                { value: 30, text: '30' },
                { value: 40, text: '40' },
                { value: 50, text: '50' },
            ],

            fields: [
                { key: "noFakturImei", label: "No Faktur / IMEI", sortable: false },
                { key: "tipe", label: "Tipe", sortable: false },
                { key: "staffFv", label: "Staff FV", sortable: false },
                { key: "keterangan", label: "Keterangan", sortable: false },
                { key: "status", label: "Status", sortable: false },
                { key: "tanggal", label: "Tanggal", sortable: true },
                { key: "action", label: "Action" },
            ],

            duplicateFields: [
                { key: 'no_faktur', label: 'No Faktur', sortable: true },
                { key: 'barang', label: 'Barang', sortable: true },
                { key: 'spec', label: 'Spec', sortable: true },
                { key: 'imei', label: 'IMEI', sortable: true },
                { key: 'status', label: 'Status', sortable: true },
            ],

            items: [],
            duplicateItems: [],
            activeItem: {},
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,
        }
    },

    methods: {
        init() {
            this.initStatus = 1
        },

        getItems(page = 1) {
            if (this.$helper.isBlank(this.filter.noFaktur)) {
                this.$helper.toastError(this, "Silakan masukkan No. Faktur untuk mencari")
                return
            }

            this.isBusy = true
            this.isLoadingTable = true
            this.items = []

            if (isNaN(page)) {
                this.currentPage = 1
            } else {
                this.currentPage = page
            }

            let param = this.$helper.getBasePagedQuery(this.currentPage, this.perPage)
            param += "&filterNoFaktur=" + this.filter.noFaktur

            if (!this.$helper.isBlank(this.sortBy)) {
                param += "&sortBy=" + this.sortBy
                param += "&sortDesc=" + this.sortDesc
            }

            this.$axios.get("history-fovid" + param).then(res => {
                this.items = res.data.data.items
                this.totalRows = res.data.data.total || 0
                this.initTable = 1
            })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.duplicateItems = error.response.data.data
                        this.$bvModal.show("m-duplicate-nf")
                    } else {
                        this.$helper.parseError(this, error)
                    }
                })
                .finally(() => {
                    this.isBusy = false
                    this.isLoadingTable = false
                })
        },

        selectInventory(item) {
            this.$bvModal.hide("m-duplicate-nf")

            this.isBusy = true
            this.isLoadingTable = true

            let param = this.$helper.getBasePagedQuery(this.currentPage, this.perPage)
            param += "&filterInvenId=" + item.id

            if (!this.$helper.isBlank(this.sortBy)) {
                param += "&sortBy=" + this.sortBy
                param += "&sortDesc=" + this.sortDesc
            }

            this.$axios.get("history-fovid" + param).then(res => {
                this.items = res.data.data.items
                this.totalRows = res.data.data.total || 0
                this.initTable = 1
            })
                .catch(error => {
                    this.$helper.parseError(this, error)
                })
                .finally(() => {
                    this.isBusy = false
                })
        },


        async checkImgUrlStatus(url) {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = url;
            })
        },

        async setAssetThumbnailSource(asset) {
            this.$set(asset, 'computedThumbnailUrl', '/img/image-loading.gif');

            let computedUrl;

            if (asset.type === 'video') {
                computedUrl = '/img/video-thumbnail.png';
            } else if (asset.type === 'image') {
                if (asset.url_compressed) {
                    const isCompressedAccessible = await this.checkImgUrlStatus(asset.url_compressed);
                    computedUrl = isCompressedAccessible ? asset.url_compressed : (asset.url || '/img/img-preview.png');
                } else if (asset.url) {
                    const isOriginalAccessible = await this.checkImgUrlStatus(asset.url);
                    computedUrl = isOriginalAccessible ? asset.url : '/img/img-preview.png';
                } else {
                    computedUrl = '/img/img-preview.png';
                }
            } else {
                computedUrl = '/img/img-preview.png';
            }

            this.$set(asset, 'computedThumbnailUrl', computedUrl);
        },

        async setMediaPreviewSource(asset) {
            if (asset.type === 'video') {
                this.mediaPreview = {
                    type: 'video',
                    url: asset.url,
                    computedUrl: null,
                    url_compressed: asset.url_compressed,
                    mime_type: asset.mime_type,
                    isActive: true
                }
                return
            }

            this.mediaPreview = {
                type: 'image',
                url: asset.url || null,
                computedUrl: '/img/image-loading.gif',
                url_compressed: asset.url_compressed,
                mime_type: asset.mime_type,
                isActive: true
            }
            let previewUrl = '/img/img-preview.png'
            if (asset.url_compressed) {
                const isCompressedAccessible = await this.checkImgUrlStatus(asset.url_compressed)
                previewUrl = isCompressedAccessible ? asset.url_compressed : (asset.url || '/img/img-preview.png')
            } else if (asset.url) {
                const isOriginalAccessible = await this.checkImgUrlStatus(asset.url)
                previewUrl = isOriginalAccessible ? asset.url : '/img/img-preview.png'
            }

            this.mediaPreview.computedUrl = previewUrl
        },

        async showDetail(item) {
            this.activeItem = item
            this.mediaPreview = {}

            this.$bvModal.show("m-detail")

            if (item.assets && item.assets.length > 0) {
                for (let i = 0; i < item.assets.length; i++) {
                    const asset = item.assets[i];
                    asset.isActive = i === 0;
                    this.setAssetThumbnailSource(asset);
                }

                await this.setMediaPreviewSource(item.assets[0]);
            }
        },

        async onClickAsset(asset) {
            if (this.activeItem.assets) {
                this.activeItem.assets.forEach(a => a.isActive = false)
            }

            asset.isActive = true

            await this.setMediaPreviewSource(asset);
        },

        openMediaInNewTab(url) {
            if (!this.activeItem) return

            const activeAsset = (this.activeItem.assets || []).find(a => a.isActive) || null

            const finalUrl = (activeAsset && activeAsset.url) ? activeAsset.url : url

            if (finalUrl) {
                window.open(finalUrl, '_blank')
            }
        },


        getStatusVariant(status) {
            switch (status) {
                case 'TAMBAH': return 'success'
                case 'UBAH': return 'warning'
                case 'HAPUS': return 'danger'
                default: return 'secondary'
            }
        },

        getInventoryStatusVariant(status) {
            switch (status) {
                case 1: return 'warning'
                case 2: return 'success'
                case 3: return 'info'
                case 4: return 'secondary'
                case 5: return 'danger'
                default: return 'light'
            }
        },

        getInventoryStatusName(status) {
            switch (status) {
                case 1: return 'PENDING'
                case 2: return 'READY'
                case 3: return 'TERJUAL'
                case 4: return 'RETUR'
                case 5: return 'HILANG'
                default: return 'LAINNYA'
            }
        },

        onClearMediaPreview() {
            this.mediaPreview = {
                type: null,
                url: null,
                computedUrl: null,
                url_compressed: null,
                mime_type: null,
                isActive: false
            }
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage
            this.getItems(this.currentPage)
        },

        onPageChange(newPage) {
            this.currentPage = newPage
            this.getItems(this.currentPage)
        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy
            this.sortDesc = ctx.sortDesc
            this.getItems()
        },
    },

    created() {
        this.init()
    }
}
</script>

<style scoped>
.img-fluid {
    max-width: 100%;
    height: auto;
}

.media-container {
    height: 0;
    padding-top: 56.25%;
    position: relative;
}

.media-preview {
    max-height: 100%;
    max-width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border: 1px solid #dee2e6;
    border-radius: .25rem;
    object-fit: cover;
    background-color: #dee2e6;
    left: 50%;
    transform: translateX(-50%);
}

:not(:root):-webkit-full-screen {
    object-fit: contain !important;
}

.media-scroll {
    margin-top: 10px;
    height: 70px;
    width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    background-color: #dee2e6;
    white-space: nowrap;
}

.media-scroll-content {
    display: inline-block !important;
    height: 100%;
    width: 20%;
}

.media-scroll-preview.active {
    border: 5px solid #65c2e4;
}

.media-scroll-preview:hover {
    opacity: 0.5;
}

.media-scroll-preview {
    height: 100%;
    width: 100%;
    border: 5px solid #dee2e6;
    border-radius: .25rem;
    object-fit: cover;
    background-color: #dee2e6;
    cursor: pointer;
}

.border-responsive {
    margin-top: 10px;
    border-top: 1px solid #c8ced3;
}

.media-disclaimer {
    text-align: justify;
    text-justify: inter-word;
}

.detail-info {
    font-size: 0.9rem;
}

.detail-info>div {
    margin-bottom: 0.25rem;
}

@media only screen and (min-width: 576px) {
    .media-scroll {
        height: 100px;
    }
}

@media only screen and (min-width: 992px) {
    .border-responsive {
        margin-top: 0px;
        border-top: none;
        border-left: 1px solid #c8ced3;
    }
}

@media only screen and (min-width: 1200px) {
    .media-scroll {
        height: 150px;
    }
}
</style>